import { Node } from '../../node'
import { FormulaQuestionNode } from '../../questionNode/formulaQuestionNode/formulaQuestionNode'
import { LabelCheckQuestionNode } from '../../questionNode/labelCheckQuestionNode/labelCheckQuestionNode'
import { TimeMeasurementQuestionNode } from '../../questionNode/timeMeasurementQuestionNode/timeMeasurementQuestionNode'
import type { NodeDict, NodeId } from '../../node'

/**
 * クローンされたノードが持つ他ノードへの参照を、コピー先のノード id に張り替える
 *
 * idMapping 内にマッピングが存在する場合、その key に対応する node は、今回コピー元となったノードである。
 * a) idMapping の key に、参照先の node.id が存在する -> 今回コピーされたコピー先のノードを参照するように修正する必要がある
 * b) idMapping の key に、参照先の node.id が存在しない -> セクション跨ぎが発生しない場合、発生し得ない。セクション跨ぎがある場合、参照先は変える必要がない
 *
 * @param node 参照の張り替え対象のノード
 * @param clonedDescendantNodes クローンされた子孫ノードの辞書
 * @param idMapping コピー元のノード id とコピー先のノード id の対応
 */
export const swapReferencedNodeIds = (
  node: Node,
  clonedDescendantNodes: NodeDict,
  idMapping: Map<NodeId, NodeId>,
) => {
  swapFormulaReferences(node, idMapping)
  swapTimeMeasurementReferences(node, idMapping)
  swapLabelCheckReferences(node, idMapping)

  for (const nodeId of node.nodes) {
    const childNode = clonedDescendantNodes[nodeId]
    Node.requireNode(childNode)
    swapReferencedNodeIds(childNode, clonedDescendantNodes, idMapping)
  }
}

/** 計算式のオペランドの参照 */
const swapFormulaReferences = (
  node: Node,
  idMapping: Map<NodeId, NodeId>,
): void => {
  if (!FormulaQuestionNode.isFormulaQuestionNode(node)) {
    return
  }
  node.question.formulaDefTokens.forEach(token => {
    if (token.type === 'question' && token.questionNodeId) {
      const newNodeId = idMapping.get(token.questionNodeId)
      if (newNodeId !== undefined) {
        token.questionNodeId = newNodeId
      }
    }
  })
}

/** 時間計測の開始・終了の日時質問の参照 */
const swapTimeMeasurementReferences = (
  node: Node,
  idMapping: Map<NodeId, NodeId>,
): void => {
  if (!TimeMeasurementQuestionNode.isTimeMeasurementQuestionNode(node)) {
    return
  }
  const newStartNodeId = idMapping.get(node.question.startQuestionNodeId)
  if (newStartNodeId !== undefined) {
    node.question.startQuestionNodeId = newStartNodeId
  }
  const newEndNodeId = idMapping.get(node.question.endQuestionNodeId)
  if (newEndNodeId !== undefined) {
    node.question.endQuestionNodeId = newEndNodeId
  }
}

/** ラベル検査の基準時間・参照テキストの質問の参照 */
const swapLabelCheckReferences = (
  node: Node,
  idMapping: Map<NodeId, NodeId>,
): void => {
  if (!LabelCheckQuestionNode.isLabelCheckQuestionNode(node)) {
    return
  }
  const nodeId = node.question.baseTimeReferenceQuestion?.nodeId
  const newNodeId = nodeId ? idMapping.get(nodeId) : undefined
  if (newNodeId !== undefined && node.question.baseTimeReferenceQuestion) {
    node.question.baseTimeReferenceQuestion.nodeId = newNodeId
  }

  const refTextNodeId = node.question.referenceTextQuestion?.nodeId
  const newRefTextNodeId = refTextNodeId
    ? idMapping.get(refTextNodeId)
    : undefined
  if (newRefTextNodeId !== undefined && node.question.referenceTextQuestion) {
    node.question.referenceTextQuestion.nodeId = newRefTextNodeId
  }
}
